import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import {
  ArrowLeft, CheckCircle2, Circle, Clock, SkipForward, Save, RotateCcw, PlayCircle, ListChecks,
} from "lucide-react";

const STATUS_BADGES = {
  pending:     { label: "Pending",     bg: "#F3F4F6", fg: "#525252", icon: Circle },
  in_progress: { label: "In progress", bg: "#FEF3C7", fg: "#92400E", icon: Clock },
  done:        { label: "Done",        bg: "#D1FAE5", fg: "#065F46", icon: CheckCircle2 },
  skipped:     { label: "Skipped",     bg: "#E5E7EB", fg: "#525252", icon: SkipForward },
};

/**
 * /clients/:id/onboarding — 5-step playbook that walks a new paying client from
 * kickoff → first-campaign-live → week-1 review. Writes to clients.onboarding_playbook.
 */
export default function ClientOnboarding() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data: body } = await api.get(`/clients/${id}/onboarding`);
      setData(body);
    } catch (err) {
      toast.error(formatApiError(err) || "Load failed");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (id) load(); /* eslint-disable-line */ }, [id]);

  const updateStep = async (stepKey, patch) => {
    setSavingKey(stepKey);
    try {
      const { data: body } = await api.put(`/clients/${id}/onboarding/${stepKey}`, patch);
      setData((d) => ({
        ...d,
        progress: body.progress,
        steps: d.steps.map((s) => s.key === stepKey ? body.step : s),
      }));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingKey(null); }
  };

  const completeStep = async (stepKey) => {
    setSavingKey(stepKey);
    try {
      const { data: body } = await api.post(`/clients/${id}/onboarding/${stepKey}/complete`);
      setData((d) => ({
        ...d,
        progress: body.progress,
        steps: d.steps.map((s) => s.key === stepKey ? body.step : s),
      }));
      if (body.progress?.all_complete) {
        toast.success("All onboarding steps complete! 🎉 Admin notified via email.");
      } else {
        toast.success(`"${body.step.label}" marked done`);
      }
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingKey(null); }
  };

  const resetAll = async () => {
    if (!window.confirm("Reset the entire playbook back to pending? Due dates will be recalculated from today.")) return;
    try {
      const { data: body } = await api.post(`/clients/${id}/onboarding/reset`);
      setData((d) => ({ ...d, steps: body.steps, progress: body.progress, started_at: new Date().toISOString() }));
      toast.success("Playbook reset — all 5 steps back to pending.");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="client-onboarding-page">
      <Toaster richColors position="top-right" />
      <div className="max-w-5xl mx-auto px-6 py-10">
        <Link to={`/clients/${id}`} className="inline-flex items-center gap-2 text-sm font-medium text-neutral-500 hover:text-black mb-6" data-testid="back-to-client-link">
          <ArrowLeft className="w-4 h-4" /> back to client
        </Link>

        <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-mono flex items-center gap-2">
              <ListChecks className="w-3.5 h-3.5" /> 5-step onboarding playbook
            </div>
            <h1 className="font-display text-3xl font-bold mt-1" data-testid="client-onboarding-title">
              Onboarding · {data?.business_name || "…"}
            </h1>
            {data?.started_at && (
              <div className="text-xs text-neutral-500 mt-2 font-mono">
                Started {new Date(data.started_at).toLocaleDateString()}
              </div>
            )}
          </div>
          {!loading && data && (
            <button data-testid="reset-playbook-btn" onClick={resetAll}
                    className="inline-flex items-center gap-2 px-3 py-1.5 border border-neutral-300 text-xs font-semibold hover:border-rose-500 hover:text-rose-700">
              <RotateCcw className="w-3.5 h-3.5" /> Reset playbook
            </button>
          )}
        </div>

        {loading || !data ? (
          <div className="p-10 text-center text-sm text-neutral-500 border border-neutral-200 bg-white">Loading…</div>
        ) : (
          <>
            <ProgressBar progress={data.progress} />
            <div className="grid gap-4 mt-6">
              {data.steps.map((step, i) => (
                <StepCard
                  key={step.key}
                  index={i + 1}
                  step={step}
                  saving={savingKey === step.key}
                  onUpdate={(patch) => updateStep(step.key, patch)}
                  onComplete={() => completeStep(step.key)}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}


function ProgressBar({ progress }) {
  const pct = progress?.percent || 0;
  return (
    <div className="bg-white border border-neutral-200 p-5" data-testid="onboarding-progress">
      <div className="flex items-center justify-between mb-3 text-sm">
        <div className="font-semibold">
          <span data-testid="progress-percent">{pct}%</span> complete
          <span className="text-neutral-500 font-normal ml-2">
            ({progress?.done || 0} done · {progress?.in_progress || 0} in progress · {progress?.pending || 0} pending)
          </span>
        </div>
        {progress?.all_complete && (
          <span className="text-xs font-mono uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1">
            Fully onboarded ✓
          </span>
        )}
      </div>
      <div className="h-2 bg-neutral-100">
        <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}


function StepCard({ index, step, saving, onUpdate, onComplete }) {
  const badge = STATUS_BADGES[step.status] || STATUS_BADGES.pending;
  const StatusIcon = badge.icon;
  const [notes, setNotes] = useState(step.notes || "");
  const [notesChanged, setNotesChanged] = useState(false);
  useEffect(() => { setNotes(step.notes || ""); setNotesChanged(false); }, [step.notes]);

  const changeStatus = (newStatus) => onUpdate({ status: newStatus });

  const saveNotes = () => {
    if (!notesChanged) return;
    onUpdate({ notes });
    setNotesChanged(false);
  };

  const isDone = step.status === "done";
  const dueDateStr = step.due_date ? new Date(step.due_date).toLocaleDateString() : "—";

  return (
    <div data-testid={`step-card-${step.key}`} className={`bg-white border p-5 ${isDone ? "border-emerald-200" : "border-neutral-200"}`}>
      <div className="flex items-start gap-4 mb-4">
        <div className={`w-10 h-10 flex items-center justify-center font-display text-lg font-bold shrink-0 ${isDone ? "bg-emerald-50 text-emerald-700" : "bg-neutral-100 text-neutral-700"}`}>
          {index}
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h3 className="font-display text-xl font-bold">{step.label}</h3>
            <span data-testid={`step-${step.key}-status`} className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-mono px-2 py-0.5"
                  style={{ background: badge.bg, color: badge.fg }}>
              <StatusIcon className="w-3 h-3" /> {badge.label}
            </span>
          </div>
          <div className="text-xs text-neutral-500 mt-1 font-mono">
            Owner: {step.owner || "—"} · Due {dueDateStr}
            {step.completed_at && ` · completed ${new Date(step.completed_at).toLocaleDateString()}`}
          </div>
        </div>
      </div>

      {step.subtasks?.length > 0 && (
        <ul className="pl-14 mb-4 space-y-1.5 list-disc list-inside text-sm text-neutral-700">
          {step.subtasks.map((t, i) => (
            <li key={i} data-testid={`step-${step.key}-subtask-${i}`}>{t}</li>
          ))}
        </ul>
      )}

      <div className="pl-14 space-y-3">
        <div>
          <label className="block text-[10px] uppercase tracking-widest font-mono text-neutral-500 mb-1">Notes</label>
          <textarea data-testid={`step-${step.key}-notes`} value={notes}
                    onChange={(e) => { setNotes(e.target.value); setNotesChanged(true); }}
                    onBlur={saveNotes}
                    rows={2} placeholder="Follow-up details, blockers, call summary…"
                    className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:border-neutral-900 resize-none" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {step.status !== "done" && (
            <button data-testid={`step-${step.key}-complete`} onClick={onComplete} disabled={saving}
                    className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50">
              <CheckCircle2 className="w-3.5 h-3.5" /> Mark done
            </button>
          )}
          {step.status === "pending" && (
            <button data-testid={`step-${step.key}-start`} onClick={() => changeStatus("in_progress")} disabled={saving}
                    className="inline-flex items-center gap-2 px-3 py-1.5 border border-amber-300 text-amber-800 text-xs font-semibold hover:bg-amber-50 disabled:opacity-50">
              <PlayCircle className="w-3.5 h-3.5" /> Start
            </button>
          )}
          {step.status !== "skipped" && step.status !== "done" && (
            <button data-testid={`step-${step.key}-skip`} onClick={() => changeStatus("skipped")} disabled={saving}
                    className="inline-flex items-center gap-2 px-3 py-1.5 border border-neutral-300 text-neutral-600 text-xs font-semibold hover:border-neutral-500 disabled:opacity-50">
              <SkipForward className="w-3.5 h-3.5" /> Skip
            </button>
          )}
          {(step.status === "done" || step.status === "skipped") && (
            <button data-testid={`step-${step.key}-reopen`} onClick={() => changeStatus("pending")} disabled={saving}
                    className="inline-flex items-center gap-2 px-3 py-1.5 border border-neutral-300 text-neutral-600 text-xs font-semibold hover:border-neutral-500 disabled:opacity-50">
              <RotateCcw className="w-3.5 h-3.5" /> Reopen
            </button>
          )}
          {notesChanged && (
            <button onClick={saveNotes} className="inline-flex items-center gap-2 px-3 py-1.5 border border-neutral-900 text-xs font-semibold hover:bg-neutral-900 hover:text-white">
              <Save className="w-3.5 h-3.5" /> Save notes
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
